import { Component, EventEmitter, Input, OnInit, Output, forwardRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, FormsModule } from '@angular/forms';

@Component({
    selector: 'app-input-otp',
    standalone: true,
    imports: [CommonModule, FormsModule],
    templateUrl: './app-input-otp.html',
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => AppInputOtp),
            multi: true
        }
    ]
})
export class AppInputOtp implements ControlValueAccessor, OnInit {
    @Input() label: string = '';
    @Input() length: number = 6;
    @Input() disabled: boolean = false;
    @Input() error: string = '';
    @Input() size: 'xs' | 'sm' | 'md' | 'lg' = 'md';
    @Input() fullWidth: boolean = false;

    @Output() valueChange = new EventEmitter<string>();

    otpValues: string[] = [];

    onChange: any = () => { };
    onTouched: any = () => { };

    ngOnInit(): void {
        this.otpValues = Array(this.length).fill('');
    }

    writeValue(value: any): void {
        if (value) {
            const digits = value.toString().slice(0, this.length).split('');
            this.otpValues = Array(this.length).fill('').map((_, i) => digits[i] || '');
        } else {
            this.otpValues = Array(this.length).fill('');
        }
    }

    registerOnChange(fn: any): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }

    onInput(event: any, index: number) {
        const input = event.target as HTMLInputElement;
        const val = input.value.replace(/\D/g, '').slice(-1);

        this.otpValues[index] = val;
        input.value = val;

        if (val && index < this.length - 1) {
            const nextInput = document.getElementById(`otp-${index + 1}`) as HTMLInputElement;
            nextInput?.focus();
        }
        this.emitValue();
    }

    onKeyDown(event: KeyboardEvent, index: number) {
        if (event.key === 'Backspace' && !this.otpValues[index] && index > 0) {
            const prevInput = document.getElementById(`otp-${index - 1}`) as HTMLInputElement;
            prevInput?.focus();
        }
    }

    onPaste(event: ClipboardEvent) {
        event.preventDefault();
        const pastedData = event.clipboardData?.getData('text') || '';
        const digits = pastedData.replace(/\D/g, '').slice(0, this.length).split('');

        digits.forEach((digit, i) => {
            this.otpValues[i] = digit;
        });

        const lastIdx = Math.min(digits.length, this.length - 1);
        const lastInput = document.getElementById(`otp-${lastIdx}`) as HTMLInputElement;
        lastInput?.focus();

        this.emitValue();
    }

    private emitValue() {
        const fullValue = this.otpValues.join('');
        this.onChange(fullValue);
        this.onTouched();
        this.valueChange.emit(fullValue);
    }

    get sizeClasses(): string {
        const map: Record<string, string> = {
            xs: 'w-10 h-14 text-xl',
            sm: 'w-12 h-16 text-2xl',
            md: 'w-14 h-20 text-3xl',
            lg: 'w-16 h-24 text-4xl',
        };
        return map[this.size] ?? map['md'];
    }

    get gapClass(): string {
        const map: Record<string, string> = {
            xs: 'gap-2',
            sm: 'gap-3',
            md: 'gap-4',
            lg: 'gap-5',
        };
        return map[this.size] ?? map['md'];
    }

    get distributeClass(): string {
        if (this.fullWidth) return 'justify-between w-full';
        return `justify-start ${this.gapClass}`;
    }
}
